import {
  type Collaborator,
  WORKSPACE_VERSION_GROUPS,
  type Workspace,
  type WorkspaceVersionGroup
} from '@stratumn/studio-shared';
import { useState } from 'react';
import { AddFilterButton } from './AddFilterButton';
import type { FilterOperator, FilterType } from './types';
import { UserFilterPicker } from './UserFilterPicker';
import { useGenerationFilters } from './useGenerationFilters';
import { VersionGroupFilterPicker } from './VersionGroupFilterPicker';
import { WorkspaceFilterPicker } from './WorkspaceFilterPicker';

type GenerationFilterBarProps = {
  workspaces: Workspace[];
  collaborators: Collaborator[];
};

export const GenerationFilterBar = ({
  workspaces,
  collaborators
}: GenerationFilterBarProps) => {
  const {
    filters,
    activeFilterTypes,
    setWorkspaceFilter,
    setVersionGroupFilter,
    setUserFilter,
    removeFilter
  } = useGenerationFilters();

  /**
   * Track which filter types have been "opened" by the user via the + button.
   * This lets us show a picker even when no values are selected yet.
   */
  const [pendingFilters, setPendingFilters] = useState<Set<FilterType>>(
    new Set()
  );

  const handleAddFilter = (type: FilterType) => {
    setPendingFilters(prev => new Set(prev).add(type));
  };

  const handleRemoveFilter = (type: FilterType) => {
    removeFilter(type);
    setPendingFilters(prev => {
      const next = new Set(prev);
      next.delete(type);
      return next;
    });
  };

  /** A filter should be visible if it has values OR was just added via "+" */
  const visibleFilterTypes = new Set([...activeFilterTypes, ...pendingFilters]);

  const handleWorkspaceChange = (ids: string[], operator: FilterOperator) => {
    setWorkspaceFilter(ids, operator);
    if (ids.length === 0) {
      setPendingFilters(prev => {
        const next = new Set(prev);
        next.delete('workspace');
        return next;
      });
    }
  };

  const handleVersionGroupChange = (
    groups: string[],
    operator: FilterOperator
  ) => {
    const validGroups = groups.filter((g): g is WorkspaceVersionGroup =>
      (WORKSPACE_VERSION_GROUPS as readonly string[]).includes(g)
    );
    setVersionGroupFilter(validGroups, operator);
    if (groups.length === 0) {
      setPendingFilters(prev => {
        const next = new Set(prev);
        next.delete('version_group');
        return next;
      });
    }
  };

  const handleUserChange = (ids: string[], operator: FilterOperator) => {
    setUserFilter(ids, operator);
    if (ids.length === 0) {
      setPendingFilters(prev => {
        const next = new Set(prev);
        next.delete('user');
        return next;
      });
    }
  };

  const allFilterTypes: FilterType[] = ['workspace', 'version_group', 'user'];
  const inactiveFilterTypes = allFilterTypes.filter(
    t => !visibleFilterTypes.has(t)
  );

  return (
    <div className="flex flex-wrap items-center gap-2">
      {visibleFilterTypes.has('workspace') && (
        <WorkspaceFilterPicker
          autoOpen={
            pendingFilters.has('workspace') &&
            filters.workspace_id.values.length === 0
          }
          onChange={handleWorkspaceChange}
          onRemove={() => handleRemoveFilter('workspace')}
          operator={filters.workspace_id.operator}
          selectedIds={filters.workspace_id.values}
          workspaces={workspaces}
        />
      )}

      {visibleFilterTypes.has('version_group') && (
        <VersionGroupFilterPicker
          autoOpen={
            pendingFilters.has('version_group') &&
            filters.version_group.values.length === 0
          }
          onChange={handleVersionGroupChange}
          onRemove={() => handleRemoveFilter('version_group')}
          operator={filters.version_group.operator}
          selectedGroups={filters.version_group.values}
        />
      )}

      {visibleFilterTypes.has('user') && (
        <UserFilterPicker
          autoOpen={
            pendingFilters.has('user') && filters.created_by.values.length === 0
          }
          collaborators={collaborators}
          onChange={handleUserChange}
          onRemove={() => handleRemoveFilter('user')}
          operator={filters.created_by.operator}
          selectedIds={filters.created_by.values}
        />
      )}

      {inactiveFilterTypes.length > 0 && (
        <AddFilterButton
          activeFilterTypes={[...visibleFilterTypes]}
          onAdd={handleAddFilter}
        />
      )}
    </div>
  );
};
